import { Injectable } from '@angular/core';

export interface MlsInfo {
	name: string;
	brand: string;
}

interface OnboardingProviderEntry {
	canonical: string;
	normalized: string;
}

const SUPPORTED_ONBOARDING_PROVIDERS = ['stellar_mls', 'stellar', 'miami_realtors_mls', 'miamire'];
const KNOWN_NON_MLS_ONBOARDING_PROVIDERS = ['elm_street_idx_broker', 'elm_street', 'elm'];

@Injectable({
	providedIn: 'root'
})
export class MlsService {
	private readonly fallbackBrand = './assets/images/logo-keyou-color.svg';

	constructor() {}

	getOnboardingProviders(values: string[]): string[] {
		const providersByKey = new Map<string, OnboardingProviderEntry>();

		for (const entry of (values || [])
			.map(value => this.toOnboardingProviderEntry(value))
			.filter((provider): provider is OnboardingProviderEntry => !!provider)) {
			const key = this.getOnboardingProviderKey(entry);
			const currentEntry = providersByKey.get(key);

			if (!currentEntry || this.shouldReplaceOnboardingProviderEntry(currentEntry, entry)) {
				providersByKey.set(key, entry);
			}
		}

		const providers = Array.from(providersByKey.values());

		if (providers.some(provider => KNOWN_NON_MLS_ONBOARDING_PROVIDERS.includes(provider.normalized))) {
			return providers
				.filter(provider => SUPPORTED_ONBOARDING_PROVIDERS.includes(provider.normalized))
				.map(provider => provider.canonical);
		}

		return providers.map(provider => provider.canonical);
	}

	getListInfo(value: string): MlsInfo {
		switch (this.normalizeProviderValue(value)) {
			case 'elm_street_idx_broker':
			case 'elm_street':
			case 'elm':
				return {
					name: 'Elm Street',
					brand: this.fallbackBrand
				};
			case 'stellar':
			case 'stellar_mls':
				return {
					name: 'Stellar MLS',
					brand: './assets/images/logo-stellar-mls.png'
				};
			case 'miamire':
			case 'miami_realtors_mls':
				return {
					name: 'Miami Realtors MLS',
					brand: './assets/images/logo-miamire-mls.png'
				};
			default:
				return {
					name: this.formatProviderName(value),
					brand: this.fallbackBrand
				};
		}
	}

	formatProviderName(value: string): string {
		const normalizedValue = this.normalizeProviderValue(value);

		if (!normalizedValue) {
			return 'MLS';
		}

		return normalizedValue
			.split(/[_\s]+/)
			.filter(Boolean)
			.map(chunk => chunk.charAt(0).toUpperCase() + chunk.slice(1))
			.join(' ')
			.replace(/\bMls\b/g, 'MLS')
			.replace(/\bIdx\b/g, 'IDX');
	}

	private normalizeProviderValue(value: string | null | undefined): string {
		return value?.trim().toLowerCase() || '';
	}

	private toOnboardingProviderEntry(value: string | null | undefined): OnboardingProviderEntry | null {
		const trimmedValue = value?.trim();
		const normalizedValue = this.normalizeProviderValue(value);

		if (!trimmedValue || !normalizedValue) {
			return null;
		}

		switch (normalizedValue) {
			case 'stellar':
			case 'stellar_mls':
				return {
					canonical: 'STELLAR_MLS',
					normalized: normalizedValue
				};
			case 'miamire':
			case 'miami_realtors_mls':
				return {
					canonical: 'MIAMI_REALTORS_MLS',
					normalized: normalizedValue
				};
			default:
				return {
					canonical: trimmedValue,
					normalized: normalizedValue
				};
		}
	}

	private getOnboardingProviderKey(entry: OnboardingProviderEntry): string {
		switch (entry.canonical) {
			case 'STELLAR_MLS':
			case 'MIAMI_REALTORS_MLS':
				return entry.canonical;
			default:
				return entry.normalized;
		}
	}

	private shouldReplaceOnboardingProviderEntry(
		currentEntry: OnboardingProviderEntry,
		candidateEntry: OnboardingProviderEntry
	): boolean {
		return currentEntry.canonical === currentEntry.normalized && candidateEntry.canonical !== candidateEntry.normalized;
	}
}
